import React from 'react'

import {
    QueryInput,
    useCountChatsQuery,
    useQueryChatPreviewsQuery,
    ChatPreviewFieldsFragment,
    useUpdateChatMutation,
    namedOperations,
} from '~graphql/generated/graphql'
import {
    eqBoolFilter,
    gtFloatFilter,
    inStringArrFilter,
} from '~utils/query-helpers'
import { useRefetchOnUpdate } from '~utils/hooks/use-cached-query-refetch'
import { useChatClinics } from './clinics-provider'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'
import { useClinic } from '~contexts/clinic-context'

export type ChatPreviewsValues = {
    pinnedChats: ChatPreviewFieldsFragment[]
    localPinnedChats: ChatPreviewFieldsFragment[]
    pinChat: (chat: ChatPreviewFieldsFragment, isLocal?: boolean) => void
    unpinChat: (chat: ChatPreviewFieldsFragment, isLocal?: boolean) => void
    chatPreviews: ChatPreviewFieldsFragment[]
    chatPreviewsCount: number
    refetchChatPreviews: () => void
    page: number
    setPage: (page: number) => void
    isInitialLoad: boolean
}

export default function useChatPreviewsQuery({
    queryFilters,
    orderBy,
}: {
    queryFilters: QueryInput[]
    orderBy: string
}): ChatPreviewsValues {
    const { clinicGroup } = useClinic()
    const { clinics } = useChatClinics()
    const PAGE_SIZE = 10

    const [page, setPage] = React.useState<number>(0)
    const [isInitialLoad, setIsInitialLoad] = React.useState<boolean>(true)
    const [pinnedChatIds, setPinnedChatIds] = useLocalStorage<string[]>(
        LocalStorageKeys.pinnedChats,
        [],
    )

    const queryFiltersKey = JSON.stringify(queryFilters)
    React.useEffect(() => {
        setPage(0)
    }, [queryFiltersKey])

    // Note: the inbox intentionally does NOT filter on `chat_status` by default.
    // Assigning a status should not remove a chat from the inbox — status
    // filtering is opt-in via the inbox filter (see ChatStatusConfigMultiSelect),
    // which appends its own `chat_status` filter to `queryFilters`. The
    // whiteboard, by contrast, partitions chats into per-status columns.
    const chatsQueryFilters = [
        inStringArrFilter(
            'clinic',
            clinics.map(c => c.id),
        ),
        gtFloatFilter('message_count', 0),
        eqBoolFilter('is_hidden', false),
        ...queryFilters,
    ]

    const hasUpdatedFilters = useRefetchOnUpdate({
        value: queryFilters,
        comparisonFunction: (originalValue, newValue) => {
            return originalValue.some(
                f =>
                    f.value !==
                    newValue.find(f2 => f2.column === f.column)?.value,
            )
        },
    })

    const { data: localPinnedChatsData, refetch: refetchLocalPinnedChats } =
        useQueryChatPreviewsQuery({
            variables: {
                where: [
                    ...chatsQueryFilters,
                    inStringArrFilter('id', pinnedChatIds),
                ],
                page: 0,
                pageSize: 100,
                orderBy: {
                    column: 'last_message_time',
                    isAscending: false,
                },
            },
            skip: !pinnedChatIds.length || !clinics.length,
        })

    const { data: pinnedChatsData, refetch: refetchPinnedChats } =
        useQueryChatPreviewsQuery({
            variables: {
                where: [...chatsQueryFilters, eqBoolFilter('is_pinned', true)],
                page: 0,
                pageSize: 100,
                orderBy: {
                    column: 'last_message_time',
                    isAscending: false,
                },
            },
            skip: !clinics.length || !!clinicGroup,
        })

    const { data: chatsData, refetch: refetchChats } =
        useQueryChatPreviewsQuery({
            variables: {
                where: [
                    ...chatsQueryFilters,
                    eqBoolFilter('is_pinned', false),
                ],
                page,
                pageSize: PAGE_SIZE,
                orderBy: {
                    column: orderBy,
                    isAscending: false,
                },
            },
            onCompleted: () => {
                setIsInitialLoad(false)
            },
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: hasUpdatedFilters ? 'network-only' : undefined,
            skip: !clinics.length,
        })

    const { data: chatCountData, refetch: refetchCount } = useCountChatsQuery({
        variables: {
            where: chatsQueryFilters,
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: hasUpdatedFilters ? 'network-only' : undefined,
        skip: !clinics.length,
    })

    const [updateChat, { loading }] = useUpdateChatMutation({
        refetchQueries: [
            namedOperations.Query.queryChatPreviews,
            namedOperations.Query.countChats,
        ],
        awaitRefetchQueries: true,
    })

    const chatPreviews = chatsData?.queryChats ?? []
    const chatPreviewsCount = chatCountData?.countChats ?? 0
    const pinnedChats = pinnedChatsData?.queryChats ?? []
    const localPinnedChats = localPinnedChatsData?.queryChats ?? []

    function handleRefetch() {
        refetchPinnedChats()
        refetchLocalPinnedChats()
        refetchChats()
        refetchCount()
    }

    async function handlePinChat(
        chat: ChatPreviewFieldsFragment,
        isLocal?: boolean,
    ) {
        if (isLocal) {
            setPinnedChatIds([...pinnedChatIds, chat.id])
            refetchPinnedChats()
            return
        }

        await updateChat({
            variables: {
                id: chat.id,
                data: { is_pinned: true },
            },
        })
    }
    async function handleUnpinChat(
        chat: ChatPreviewFieldsFragment,
        isLocal?: boolean,
    ) {
        if (isLocal) {
            setPinnedChatIds(pinnedChatIds.filter(c => c !== chat.id))
            refetchPinnedChats()
            return
        }

        await updateChat({
            variables: {
                id: chat.id,
                data: { is_pinned: false },
            },
        })
    }

    return {
        pinnedChats,
        localPinnedChats,
        pinChat: handlePinChat,
        unpinChat: handleUnpinChat,
        chatPreviews,
        chatPreviewsCount,
        refetchChatPreviews: handleRefetch,

        // State
        page,
        setPage,
        isInitialLoad,
    }
}
